// @ts-nocheck
import { uploadFile, getFullUrl } from "@/lib/uploadFile";
import { validateUploadFile, MAX_MEDIA_IMAGE_SIZE, extractErrorMessage } from "@/lib/utils";

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useForm, useStore } from "@tanstack/react-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { moldFormSchema, type MoldFormData } from "@/shared/schemas/tech.schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Save, AlertTriangle, ImageIcon, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { ComboboxFK } from "@/components/shared/ComboboxFK";
import { TanstackFieldWrapper, TanstackFormControl } from "@/components/ui/tanstack-form";
import { focusFirstError } from "@/lib/form/formUtils";
import { sanitizeNullsDeep } from "@/lib/form/sanitize";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import ImageLightbox from "@/components/shared/ImageLightbox";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { MapPin, Wrench, Building2, Tag } from "lucide-react";

export interface MoldFormProps {
  id?: string;
  defaultValues?: Partial<MoldFormData>;
  onSuccess?: (newItem: { id: string; label: string; original?: any }) => void;
  onCancel?: () => void;
  isModal?: boolean;
}

export function MoldForm(props?: MoldFormProps) {
  const routeParams = (useParams({ strict: false }) as any) || {};
  const isModal = props?.isModal ?? false;
  // Trang sửa dùng tham số /molds/$moldId/edit; form mở dạng modal không lấy mã từ trang đang đứng
  const id = props?.id || (isModal ? undefined : routeParams.moldId || routeParams.id);
  const isEdit = !!id && id !== "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: initialData, isLoading } = useQuery({
    queryKey: ["mold", id],
    queryFn: () => trpcClient.technical.molds.getById.query({ id }),
    enabled: isEdit,
  });

  const createMutation = useMutation({
    mutationFn: (data: MoldFormData) => trpcClient.technical.molds.create.mutate(data as any),
    onSuccess: (result: any) => {
      toast.success("Đã tạo khuôn thành công!");
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      queryClient.invalidateQueries({ queryKey: ["dieCuts"] });
      queryClient.invalidateQueries({ queryKey: ["combobox"] });
      if (props?.onSuccess) {
        props.onSuccess({
          id: result?.id,
          label: result?.name || result?.dieCutId || "",
          original: result,
        });
      } else {
        navigate({ to: "/molds" });
      }
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (data: MoldFormData) => trpcClient.technical.molds.update.mutate({ id, data: data as any }),
    onSuccess: (result: any) => {
      toast.success("Đã cập nhật khuôn thành công!");
      queryClient.invalidateQueries({ queryKey: ["molds"] });
      queryClient.invalidateQueries({ queryKey: ["dieCuts"] });
      queryClient.invalidateQueries({ queryKey: ["mold", id] });
      queryClient.invalidateQueries({ queryKey: ["combobox"] });
      if (props?.onSuccess) {
        props.onSuccess({
          id: result?.id || id,
          label: result?.name || "",
          original: result,
        });
      } else {
        navigate({ to: "/molds" });
      }
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const form = useForm({
    defaultValues: {
      name: props?.defaultValues?.name || "",
      productName: props?.defaultValues?.productName || "",
      productId: props?.defaultValues?.productId || "",
      customerId: props?.defaultValues?.customerId || "",
      size: props?.defaultValues?.size || "",
      type: props?.defaultValues?.type || "DIE_CUT",
      location: props?.defaultValues?.location || "",
      storageZone: props?.defaultValues?.storageZone || "",
      storageRack: props?.defaultValues?.storageRack || "",
      storageShelf: props?.defaultValues?.storageShelf || "",
      storageBin: props?.defaultValues?.storageBin || "",
      machine: props?.defaultValues?.machine || "",
      image: props?.defaultValues?.image || "",
      notes: props?.defaultValues?.notes || "",
      status: props?.defaultValues?.status || "AVAILABLE",
      usageCount: (props?.defaultValues as any)?.usageCount || 0,
      maxImpressions: (props?.defaultValues as any)?.maxImpressions || 100000,
      maintenanceInterval: (props?.defaultValues as any)?.maintenanceInterval || 25000,
      version: (props?.defaultValues as any)?.version || 1,
      expectedDate: props?.defaultValues?.expectedDate || "",
      dieLength: props?.defaultValues?.dieLength || ("" as any),
      dieWidth: props?.defaultValues?.dieWidth || ("" as any),
      productLength: props?.defaultValues?.productLength || ("" as any),
      productWidth: props?.defaultValues?.productWidth || ("" as any),
      ups: props?.defaultValues?.ups || ("" as any),
      isShared: props?.defaultValues?.isShared || false,
      vendorId: props?.defaultValues?.vendorId || "",
      outsourceCost: (props?.defaultValues as any)?.outsourceCost || ("" as any),
    } as any,
    validators: {
      onChange: moldFormSchema,
    },
    onSubmit: async ({ value }) => {
      if (duplicateErrors.length > 0) {
        toast.error("Vui lòng sửa lỗi trùng lặp dữ liệu trước khi lưu.");
        return;
      }
      if (isEdit) {
        await updateMutation.mutateAsync(value as any);
      } else {
        await createMutation.mutateAsync(value as any);
      }
    },
  });

  const { data: existingMolds = [] } = useQuery({
    queryKey: ["molds-all-check"],
    queryFn: () => trpcClient.technical.molds.list.query({ limit: 1000 }).then((res) => res.items as any[]),
  });

  const nameValue = useStore(form.store, (s: any) => s.values.name);

  const duplicateErrors = React.useMemo(() => {
    if (!existingMolds.length) return [];
    if (!nameValue) return [];
    
    const nInput = nameValue.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
    
    return existingMolds.filter((c: any) => {
      // trang sửa mở theo mã: so với id thật của bản ghi đang sửa
      if (isEdit && (c.id === id || c.id === initialData?.id)) return false;
      const nCName = (c.name || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
      return nCName === nInput && nInput.length > 3;
    });
  }, [existingMolds, nameValue, isEdit, id, initialData?.id]);

  useEffect(() => {
    if (initialData && isEdit) {
      const safeData = sanitizeNullsDeep(initialData);
      Object.keys(safeData).forEach((key) => {
        // trường trống trong DB giữ giá trị mặc định của form (không đổi ô nhập sang undefined)
        if (key in form.state.values && (safeData as any)[key] !== undefined) {
          form.setFieldValue(key as any, (safeData as any)[key]);
        }
      });
    }
  }, [initialData, isEdit]);

  const customerId = useStore(form.store, (s: any) => s.values.customerId);
  const typeId = useStore(form.store, (s: any) => s.values.type);

  if (isEdit && isLoading) return <div className="p-8 text-center text-slate-500">Đang tải dữ liệu...</div>;

  const formContent = (
    <div className="bg-white border rounded-lg shadow-sm p-5 space-y-6">

        {duplicateErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-red-800">Lỗi: Trùng lặp dữ liệu!</p>
              <ul className="mt-1 space-y-0.5 text-red-700">
                {duplicateErrors.map((c: any) => (
                  <li key={c.id}>• {c.code || c.id.substring(0, 8)} — {c.name}</li>
                ))}
              </ul>
            </div>
          </div>
        )}


        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="col-span-1 md:col-span-2">
            <form.Field
              name="name"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Tên khuôn" required>
                  <TanstackFormControl>
                    <Input className="h-9" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-1">
            <form.Field
              name="type"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Loại khuôn">
                  <TanstackFormControl>
                    <ComboboxConfigMaster categoryCode="mold_types" value={field.state.value} onChange={field.handleChange} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-1">
            <form.Field
              name="status"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Trạng thái">
                  <TanstackFormControl>
                    <ComboboxConfigMaster categoryCode="tooling_status" value={field.state.value} onChange={field.handleChange} placeholder="Chọn trạng thái..." />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>

          <div className="col-span-1 md:col-span-2">
            <form.Field
              name="customerId"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Khách hàng">
                  <TanstackFormControl>
                    <SharedCombobox type="customer" value={field.state.value || ""} onChange={(id) => {
                      field.handleChange(id);
                      form.setFieldValue("productId", "");
                    }} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <form.Field
              name="productId"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Sản phẩm áp dụng">
                  <TanstackFormControl>
                    <SharedCombobox type="product" value={field.state.value || ""} onChange={(id) => field.handleChange(id)} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>

          <div className="col-span-1 md:col-span-1">
            <form.Field
              name="dieLength"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Dài khuôn (mm)">
                  <TanstackFormControl>
                    <Input className="h-9" type="number" min={0} step="any" value={field.state.value as string} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-1">
            <form.Field
              name="dieWidth"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Rộng khuôn (mm)">
                  <TanstackFormControl>
                    <Input className="h-9" type="number" min={0} step="any" value={field.state.value as string} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-1">
            <form.Field
              name="ups"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Số UPS">
                  <TanstackFormControl>
                    <Input className="h-9" type="number" min={0} step="any" value={field.state.value as string} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>

          {/* Section: Tọa Độ Lưu Kho Giá Kệ (12-Col Grid) */}
          <div className="col-span-1 md:col-span-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Vị Trí Lưu Kho & Tọa Độ Giá Kệ (Zone • Rack • Shelf • Bin)
                </span>
              </div>
              <span className="text-[11px] text-slate-500">
                Tự động chuẩn hóa địa chỉ lưu trữ gáy khuôn
              </span>
            </div>

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 sm:col-span-6 md:col-span-3">
                <form.Field
                  name="storageZone"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Khu/Dãy Kho (Zone)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white" 
                          placeholder="VD: KHU_A, KHO_1" 
                          value={field.state.value || ""} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              <div className="col-span-12 sm:col-span-6 md:col-span-3">
                <form.Field
                  name="storageRack"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Kệ/Giá (Rack)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white" 
                          placeholder="VD: K01, R-02" 
                          value={field.state.value || ""} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              <div className="col-span-12 sm:col-span-6 md:col-span-3">
                <form.Field
                  name="storageShelf"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Tầng (Shelf)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white" 
                          placeholder="VD: T1, TANG_2" 
                          value={field.state.value || ""} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              <div className="col-span-12 sm:col-span-6 md:col-span-3">
                <form.Field
                  name="storageBin"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Ô/Ngăn (Bin)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white" 
                          placeholder="VD: B05, Ô-12" 
                          value={field.state.value || ""} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              <div className="col-span-12">
                <form.Field
                  name="location"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Chuỗi Vị Trí Tổng Hợp (Ghi trên tem & màn hình)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white font-mono text-xs font-semibold text-slate-800" 
                          placeholder="VD: KHU_A • Kệ K01 • Tầng T1 • Ô B05" 
                          value={field.state.value || ""} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Section: Vòng Đời, Định Mức Dập & Quản Trị Maker (12-Col Grid) */}
          <div className="col-span-1 md:col-span-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Vòng Đời, Định Mức Dập & Đối Tác Gia Công Khuôn (Maker)
                </span>
              </div>
              <span className="text-[11px] text-slate-500">
                Theo dõi độ mòn dao và kiểm soát chất lượng
              </span>
            </div>

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 sm:col-span-6 md:col-span-3">
                <form.Field
                  name="maxImpressions"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Định Mức Dập Tối Đa (Lượt)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white font-mono" 
                          type="number"
                          min={0}
                          placeholder="100,000" 
                          value={field.state.value as string} 
                          onChange={(e) => field.handleChange(Number(e.target.value) || 0)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              <div className="col-span-12 sm:col-span-6 md:col-span-3">
                <form.Field
                  name="maintenanceInterval"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Chu Kỳ Bảo Dưỡng (Lượt)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white font-mono" 
                          type="number"
                          min={0}
                          placeholder="25,000" 
                          value={field.state.value as string} 
                          onChange={(e) => field.handleChange(Number(e.target.value) || 0)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              {/* Nhà làm khuôn / chi phí làm khuôn: chưa có chỗ lưu nên tạm ẩn (02/11/2026) */}
            </div>
          </div>

          <div className="col-span-1 md:col-span-4">
            <form.Field
              name="notes"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Ghi chú">
                  <TanstackFormControl>
                    <Textarea className="h-20 resize-none" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>

          <div className="col-span-1 md:col-span-4 border-t border-slate-100 pt-4">
            <form.Field
              name="image"
              children={(field) => (
                <div className="space-y-2">
                  <label className="text-xs font-semibold text-slate-700 block">
                    Hình ảnh khuôn mẫu (Thước đo, góc nghiêng dao, thực tế)
                  </label>
                  <div className="flex items-center gap-4">
                    {field.state.value ? (
                      <div className="relative w-28 h-28 rounded-lg border border-slate-200 overflow-hidden group shadow-xs">
                        <img
                          src={getFullUrl(field.state.value)}
                          alt="Mold Preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          className="absolute top-1 right-1 bg-rose-600 hover:bg-rose-700 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => field.handleChange("")}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="w-28 h-28 rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center text-slate-400 bg-slate-50">
                        <ImageIcon className="w-6 h-6 mb-1 opacity-60" />
                        <span className="text-[10px]">Chưa có ảnh</span>
                      </div>
                    )}

                    <div className="space-y-1">
                      <input
                        type="file"
                        accept="image/*"
                        id="mold-image-input"
                        className="hidden"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            validateUploadFile(file, {
                              maxSize: MAX_MEDIA_IMAGE_SIZE,
                              label: "Ảnh khuôn",
                            });
                            toast.loading("Đang tải ảnh lên...");
                            const res = await uploadFile(file);
                            field.handleChange(res.url || res.filePath || res);
                            toast.success("Tải ảnh khuôn thành công!");
                          } catch (err: any) {
                            toast.error(extractErrorMessage(err, "Lỗi tải ảnh"));
                          } finally {
                            e.target.value = "";
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs bg-white"
                        onClick={() => document.getElementById("mold-image-input")?.click()}
                      >
                        <Upload className="w-3.5 h-3.5 mr-1" /> Tải ảnh khuôn
                      </Button>
                      <p className="text-[11px] text-slate-400">
                        Định dạng PNG, JPG, WEBP tối đa 10MB
                      </p>
                    </div>
                  </div>
                </div>
              )}
            />
          </div>
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="space-y-4">
        {formContent}
        <div className="flex justify-end space-x-2 pt-2">
          <Button 
            variant="outline" 
            type="button"
            onClick={() => (props?.onCancel ? props.onCancel() : navigate({ to: "/molds" }))}
          >
            Hủy
          </Button>
          <Button 
            disabled={createMutation.isPending || updateMutation.isPending} 
            onClick={() => {
              form.handleSubmit();
              requestAnimationFrame(() => focusFirstError(form.state as any));
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? "Cập nhật" : "Tạo mới"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Khuôn Mẫu & Dao Bế", to: "/molds" },
        { label: isEdit ? (initialData?.name || "Chỉnh sửa") : "Tạo mới" },
      ]}
      title={isEdit ? `Sửa Khuôn ${(initialData as any)?.dieCutId || id}` : "Thêm Mới Khuôn Mẫu"}
      onBack={() => (props?.onCancel ? props.onCancel() : navigate({ to: "/molds" }))}
      showContextPanel={false}
      actions={
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            type="button"
            onClick={() => (props?.onCancel ? props.onCancel() : navigate({ to: "/molds" }))}
          >
            Hủy
          </Button>
          <Button 
            size="sm"
            disabled={createMutation.isPending || updateMutation.isPending} 
            onClick={() => {
              form.handleSubmit();
              requestAnimationFrame(() => focusFirstError(form.state as any));
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {isEdit ? "Cập nhật" : "Tạo mới"}
          </Button>
        </div>
      }
    >
      <div className="container mx-auto max-w-5xl p-4 md:p-6 space-y-6">
        {formContent}
      </div>
    </StandardDetailLayout>
  );
}

export default MoldForm;
